import React, { useState, useRef, useEffect, useCallback } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Icon, isWeb, isNative } from "@xsolla/xui-primitives";
import {
  useModalId,
  useResolvedTheme,
  useOverlayLayer,
  LAYER_OFFSET,
  type ThemeOverrideProps,
  type ThemeMode,
  type ProductContext,
} from "@xsolla/xui-core";
import { useClickOutside } from "@xsolla/xui-hooks";
import {
  ChevronDown,
  ChevronUp,
  ExclamationMarkCr,
  Remove,
  Search as SearchIcon,
} from "@xsolla/xui-icons-base";
import { Portal } from "./Portal";

export interface SelectOption {
  label: string;
  value: any;
  disabled?: boolean;
}

export interface SelectProps extends ThemeOverrideProps {
  value?: string;
  placeholder?: string;
  onPress?: () => void;
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  state?: "default" | "hover" | "focus" | "disable" | "error";
  disabled?: boolean;
  label?: string;
  errorMessage?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  filled?: boolean;
  iconOnly?: boolean;
  options?: (string | SelectOption)[];
  onChange?: (value: string) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  noOptionsMessage?: string;
  /** Show a clear button to reset the selected value (field variant only) */
  clearable?: boolean;
  /** Called when the clear button is pressed */
  onClear?: () => void;
  maxHeight?: number;
  /**
   * Width of the dropdown popover. By default the popover matches the Select
   * trigger's width (`100%` of the field). Pass a number (px) or any CSS width
   * string (e.g. `320`, `"24rem"`) to size the popover independently of the
   * trigger — useful when option labels are wider than the field.
   */
  popoverWidth?: number | string;
  fullWidth?: boolean;
  testID?: string;
  /** Theme mode for the dropdown overlay. Defaults to themeMode when not set. */
  overlayThemeMode?: ThemeMode;
  /** Product context for the dropdown overlay. Defaults to themeProductContext when not set. */
  overlayThemeProductContext?: ProductContext;
  /** Preferred side for the dropdown. Default: "bottom". */
  side?: "top" | "bottom";
  /** When true, the dropdown flips to the opposite side when there isn't enough viewport space. Default: false. */
  autoFlip?: boolean;
}

interface DropdownPosition {
  left: number;
  top?: number;
  bottom?: number;
  width: number;
  visible: boolean;
}

export const Select: React.FC<SelectProps> = ({
  value,
  placeholder = "Select",
  onPress,
  size = "md",
  state: externalState,
  disabled = false,
  label,
  errorMessage,
  iconLeft,
  iconRight,
  filled = true,
  iconOnly = false,
  options = [],
  onChange,
  searchable = false,
  searchPlaceholder = "Search",
  noOptionsMessage = "No results",
  clearable = false,
  onClear,
  maxHeight = 300,
  popoverWidth,
  fullWidth = true,
  testID,
  themeMode,
  themeProductContext,
  overlayThemeMode,
  overlayThemeProductContext,
  side = "bottom",
  autoFlip = false,
}) => {
  const { theme: rawTheme } = useResolvedTheme({
    themeMode,
    themeProductContext,
  });
  const theme = rawTheme as any;
  const { theme: rawOverlayTheme } = useResolvedTheme({
    themeMode: overlayThemeMode ?? themeMode,
    themeProductContext: overlayThemeProductContext ?? themeProductContext,
  });
  const overlayTheme = rawOverlayTheme as any;
  const modalId = useModalId();
  const overlayLayer = useOverlayLayer();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(value);
  const [searchValue, setSearchValue] = useState("");
  const [dropdownPosition, setDropdownPosition] =
    useState<DropdownPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Stable IDs for ARIA relationships — lazy useState so it's computed once
  const [baseId] = useState(
    () => `xui-select-${Math.random().toString(36).slice(2, 9)}`
  );
  const labelId = `${baseId}-label`;
  const listboxId = `${baseId}-listbox`;

  const isDisable = externalState === "disable" || disabled;
  const isError = externalState === "error" || !!errorMessage;
  const isFocus = externalState === "focus" || isOpen;

  const sizeStyles = theme.sizing.input(size);
  const inputColors = theme.colors.control.input;
  const overlayInputColors = overlayTheme.colors.control.input;

  const updateDropdownPosition = useCallback(() => {
    if (!isWeb || !triggerRef.current || !dropdownRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    // Read the actual rendered height — dropdown is in DOM (visibility: hidden) at this point
    const dropH = dropdownRef.current.getBoundingClientRect().height;
    const gap = sizeStyles.fieldGap;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom - gap;
    const spaceAbove = triggerRect.top - gap;

    const placeBelow = () =>
      setDropdownPosition({
        left: triggerRect.left,
        top: triggerRect.bottom + gap,
        width: triggerRect.width,
        visible: true,
      });

    const placeAbove = () =>
      setDropdownPosition({
        left: triggerRect.left,
        bottom: viewportHeight - triggerRect.top + gap,
        width: triggerRect.width,
        visible: true,
      });

    if (!autoFlip) {
      side === "top" ? placeAbove() : placeBelow();
      return;
    }

    // Auto-flip: prefer the requested side, flip only when there isn't enough room
    if (side === "top") {
      spaceAbove >= dropH || spaceAbove >= spaceBelow
        ? placeAbove()
        : placeBelow();
    } else {
      spaceBelow >= dropH || spaceBelow >= spaceAbove
        ? placeBelow()
        : placeAbove();
    }
  }, [sizeStyles.fieldGap, side, autoFlip]);

  useEffect(() => {
    if (value !== undefined) setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    if (isFocus && selectedItemRef.current && dropdownRef.current) {
      const timeoutId = setTimeout(() => {
        const selectedItem = selectedItemRef.current;
        if (selectedItem && isWeb)
          selectedItem.scrollIntoView({ block: "nearest" });
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isFocus]);

  // Keep the portaled dropdown anchored to the trigger (web only)
  // Positioning: runs whenever the dropdown becomes visible (covers both normal open
  // and forced-focus via the state prop). Resize repositions an already-open dropdown.
  useEffect(() => {
    if (!isWeb || !isFocus) return;
    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    return () => window.removeEventListener("resize", updateDropdownPosition);
  }, [isFocus, updateDropdownPosition]);

  // Close-on-scroll: only active when the user has explicitly opened the dropdown
  // (isOpen=true), NOT when isFocus is forced via the state prop. This prevents
  // Storybook / forced-focus stories from closing immediately on ambient scroll events.
  // Scrolling inside the dropdown (the options list) is ignored.
  useEffect(() => {
    if (!isWeb || !isOpen) return;
    const closeOnScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    window.addEventListener("scroll", closeOnScroll, true);
    return () => window.removeEventListener("scroll", closeOnScroll, true);
  }, [isOpen]);

  useEffect(() => {
    if (isFocus && searchable) searchInputRef.current?.focus();
  }, [isFocus, searchable]);

  useEffect(() => {
    if (!isFocus) setSearchValue("");
  }, [isFocus]);

  // Dropdown is portaled outside containerRef, so spare clicks that land in it
  useClickOutside(
    containerRef,
    useCallback((event: MouseEvent) => {
      const dropdownEl = dropdownRef.current;
      if (dropdownEl) {
        const path =
          typeof event.composedPath === "function" ? event.composedPath() : [];
        const insideDropdown = path.length
          ? path.includes(dropdownEl)
          : dropdownEl.contains(event.target as Node);
        if (insideDropdown) return;
      }
      setIsOpen(false);
    }, []),
    isWeb && isOpen
  );

  const getOptionLabel = (option: string | SelectOption) =>
    typeof option === "string" ? option : option.label;
  const getOptionValue = (option: string | SelectOption) =>
    typeof option === "string" ? option : option.value;
  const getOptionDisabled = (option: string | SelectOption) =>
    typeof option === "string" ? false : option.disabled || false;

  const filteredOptions =
    searchable && searchValue
      ? options.filter((option) =>
          getOptionLabel(option)
            .toLowerCase()
            .includes(searchValue.toLowerCase())
        )
      : options;

  const handlePress = () => {
    if (!isDisable) {
      if (onPress) onPress();
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (option: string | SelectOption) => {
    if (getOptionDisabled(option)) return;
    const val = getOptionValue(option);
    setSelectedValue(val);
    setIsOpen(false);
    if (onChange) onChange(val);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDisable) return;
    setSelectedValue(undefined);
    if (onChange) onChange("");
    if (onClear) onClear();
  };

  const isHover = externalState === "hover";
  let backgroundColor = inputColors.bg;
  let borderColor = inputColors.border;

  if (isDisable) {
    backgroundColor = inputColors.bgDisable;
    borderColor = inputColors.borderDisable;
  } else if (isFocus) {
    backgroundColor = theme.colors.control.focus.bg;
    borderColor = isError
      ? theme.colors.border.alert
      : theme.colors.border.brand;
  } else if (isError) {
    borderColor = theme.colors.border.alert;
  } else if (isHover) {
    backgroundColor = inputColors.bgHover;
    borderColor = inputColors.borderHover;
  }

  if (filled === false && !isFocus && !isError && !isHover) {
    backgroundColor = "transparent";
  }

  const currentLabel = selectedValue
    ? getOptionLabel(
        options.find((o) => getOptionValue(o) === selectedValue) ||
          selectedValue
      )
    : placeholder;

  const textColor = isDisable
    ? inputColors.textDisable
    : selectedValue
      ? inputColors.text
      : inputColors.placeholder;
  const iconColor = isDisable
    ? inputColors.textDisable
    : inputColors.placeholder;

  const dropdownTop =
    sizeStyles.height +
    (label ? sizeStyles.fontSize + sizeStyles.fieldGap : 0) +
    sizeStyles.fieldGap;

  const dropdown = isFocus && options.length > 0 && (
    <Box
      ref={dropdownRef}
      id={listboxId}
      role="listbox"
      aria-label={label || placeholder}
      data-modal-id={modalId}
      position={isNative ? "absolute" : undefined}
      top={isNative ? dropdownTop : undefined}
      width={isNative ? (popoverWidth ?? "100%") : undefined}
      backgroundColor={overlayTheme.colors.background.secondary}
      borderColor={overlayTheme.colors.border.secondary}
      borderWidth={1}
      borderRadius={overlayTheme.shape.contextMenu[size].borderRadius}
      style={{
        zIndex: overlayLayer + LAYER_OFFSET.popover,
        ...(isNative
          ? { elevation: 4 }
          : {
              position: "fixed",
              top:
                dropdownPosition?.top != null
                  ? dropdownPosition.top
                  : undefined,
              bottom:
                dropdownPosition?.bottom != null
                  ? dropdownPosition.bottom
                  : undefined,
              left: dropdownPosition?.left,
              width: popoverWidth ?? dropdownPosition?.width,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              visibility: dropdownPosition?.visible ? "visible" : "hidden",
            }),
        minWidth:
          popoverWidth == null && iconOnly ? sizeStyles.height * 3 : undefined,
      }}
    >
      {/* Search Input - Outside scrollable area (web only) */}
      {searchable && !isNative && (
        <Box
          paddingHorizontal={sizeStyles.paddingHorizontal}
          paddingVertical={sizeStyles.paddingVertical}
          borderBottomWidth={1}
          borderColor={overlayTheme.colors.border.secondary}
        >
          <Box
            flexDirection="row"
            alignItems="center"
            gap={sizeStyles.paddingHorizontal / 2}
            paddingHorizontal={4}
          >
            <Icon
              size={sizeStyles.iconSize - 2}
              color={overlayInputColors.placeholder}
            >
              <SearchIcon />
            </Icon>
            <Box
              as="input"
              ref={searchInputRef}
              flex={1}
              type="text"
              aria-label={searchPlaceholder}
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchValue(e.target.value)
              }
              placeholder={searchPlaceholder}
              style={{
                border: "none",
                outline: "none",
                background: "transparent",
                color: overlayInputColors.text,
                fontSize: sizeStyles.fontSize,
                width: "100%",
              }}
            />
          </Box>
        </Box>
      )}

      {/* Options List - Scrollable */}
      <Box
        paddingVertical={4}
        overflow="scroll"
        style={{
          maxHeight: searchable ? maxHeight - 60 : maxHeight,
          // overflowX pins the shorthand above, which otherwise scrolls both axes.
          ...(isWeb
            ? {
                overflowY: "auto",
                overflowX: "hidden",
                scrollbarWidth: "none" as const,
              }
            : {}),
        }}
      >
        {filteredOptions.length === 0 ? (
          <Box
            paddingVertical={sizeStyles.paddingVertical * 2}
            paddingHorizontal={sizeStyles.paddingHorizontal}
            alignItems="center"
          >
            <Text
              color={overlayTheme.colors.content.tertiary}
              fontSize={sizeStyles.fontSize}
            >
              {noOptionsMessage}
            </Text>
          </Box>
        ) : (
          filteredOptions.map((option, index) => {
            const optionValue = getOptionValue(option);
            const optionLabel = getOptionLabel(option);
            const isOptionDisabled = getOptionDisabled(option);
            const isSelected = optionValue === selectedValue;
            const brandColors = overlayTheme.colors.control.brand.primary;
            const contentColors = overlayTheme.colors.content;
            return (
              <Box
                testID={testID}
                key={index}
                role="option"
                aria-selected={isSelected}
                aria-disabled={isOptionDisabled || undefined}
                ref={isSelected ? selectedItemRef : undefined}
                paddingVertical={sizeStyles.paddingVertical}
                paddingHorizontal={sizeStyles.paddingHorizontal}
                onPress={
                  isOptionDisabled ? undefined : () => handleSelect(option)
                }
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                backgroundColor={isSelected ? brandColors.bg : "transparent"}
                style={{
                  ...(isWeb
                    ? {
                        cursor: isOptionDisabled ? "not-allowed" : "pointer",
                      }
                    : {}),
                  opacity: isOptionDisabled ? 0.5 : 1,
                }}
                hoverStyle={
                  !isSelected && !isOptionDisabled
                    ? { backgroundColor: overlayInputColors.bgHover }
                    : undefined
                }
              >
                <Text
                  color={
                    isSelected
                      ? contentColors.on.brand
                      : overlayTheme.colors.content.secondary
                  }
                  fontSize={sizeStyles.fontSize}
                  fontWeight="400"
                >
                  {optionLabel}
                </Text>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      testID={testID}
      ref={containerRef}
      flexDirection="column"
      gap={sizeStyles.fieldGap}
      width={fullWidth ? "100%" : undefined}
      position="relative"
    >
      {label && (
        <Text
          id={labelId}
          color={theme.colors.content.secondary}
          fontSize={sizeStyles.fontSize - 2}
          fontWeight="500"
        >
          {label}
        </Text>
      )}
      <Box
        ref={triggerRef}
        onPress={handlePress}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isFocus && options.length > 0}
        aria-controls={listboxId}
        aria-labelledby={label ? labelId : undefined}
        aria-disabled={isDisable || undefined}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        borderWidth={borderColor !== "transparent" ? 1 : 0}
        borderRadius={sizeStyles.radius}
        height={sizeStyles.height}
        width={iconOnly ? sizeStyles.height : "100%"}
        paddingHorizontal={iconOnly ? 0 : sizeStyles.paddingHorizontal}
        flexDirection="row"
        alignItems="center"
        justifyContent={iconOnly ? "center" : "flex-start"}
        gap={iconOnly ? 4 : sizeStyles.trailingGap}
        position="relative"
        style={
          isWeb ? { cursor: isDisable ? "not-allowed" : "pointer" } : undefined
        }
        hoverStyle={
          !isDisable && !isFocus && !isError
            ? {
                backgroundColor: inputColors.bgHover,
                borderColor: inputColors.borderHover,
              }
            : undefined
        }
      >
        {iconOnly ? (
          iconLeft && (
            <Box alignItems="center" justifyContent="center">
              <Icon size={sizeStyles.iconSize} color={iconColor}>
                {iconLeft}
              </Icon>
            </Box>
          )
        ) : (
          <Box
            flexDirection="row"
            alignItems="center"
            flex={1}
            minWidth={0}
            gap={sizeStyles.contentGap}
          >
            {iconLeft && (
              <Box alignItems="center" justifyContent="center" flexShrink={0}>
                <Icon size={sizeStyles.iconSize} color={iconColor}>
                  {iconLeft}
                </Icon>
              </Box>
            )}
            <Box
              flex={1}
              minWidth={0}
              height="100%"
              justifyContent="center"
              overflow="hidden"
              overflowX="hidden"
              overflowY="hidden"
            >
              {/*
                `numberOfLines={1}` clamps the label to one line with a trailing
                ellipsis instead of letting a long value run past the field;
                `title` keeps the untruncated string readable on hover.
              */}
              <Text
                color={textColor}
                fontSize={sizeStyles.fontSize}
                numberOfLines={1}
                title={currentLabel}
              >
                {currentLabel}
              </Text>
            </Box>
          </Box>
        )}
        {/* Trailing controls never shrink, so a long label cannot displace them. */}
        <Box flexDirection="row" alignItems="center" gap={4} flexShrink={0}>
          {/* Clear button (field variant only) */}
          {clearable && !iconOnly && !isDisable && selectedValue && (
            <Box
              onPress={handleClear}
              alignItems="center"
              justifyContent="center"
              style={isWeb ? { cursor: "pointer" } : undefined}
            >
              <Remove
                size={sizeStyles.iconSize}
                color={iconColor}
                variant="line"
              />
            </Box>
          )}

          {/* Error icon */}
          {isError && (
            <Icon
              size={sizeStyles.iconSize}
              color={theme.colors.content.alert.primary}
            >
              <ExclamationMarkCr />
            </Icon>
          )}
          {iconRight !== undefined ? (
            <Icon size={sizeStyles.iconSize} color={iconColor}>
              {iconRight}
            </Icon>
          ) : isFocus ? (
            <ChevronUp
              size={sizeStyles.iconSize}
              color={iconColor}
              variant="line"
            />
          ) : (
            <ChevronDown
              size={sizeStyles.iconSize}
              color={iconColor}
              variant="line"
            />
          )}
        </Box>
      </Box>

      {/* Dropdown Menu */}
      {isNative ? dropdown : <Portal>{dropdown}</Portal>}

      {isError && errorMessage && (
        <Text
          color={theme.colors.content.alert.primary}
          fontSize={sizeStyles.fontSize - 2}
          style={{ lineHeight: sizeStyles.lineHeight + "px" }}
        >
          {errorMessage}
        </Text>
      )}
    </Box>
  );
};
